/**
 * Determines the appropriate mask and settings for payment fields based on field name
 */
export interface FieldMaskConfig {
  mask?: string;
  showMaskTyped?: boolean;
  hiddenInput?: boolean;
  type?: string;
}

/**
 * Get mask configuration for payment fields
 * @param fieldName - The name of the field
 * @returns Mask configuration object
 */
export function getPaymentFieldMask(
  fieldName: string
): FieldMaskConfig | undefined {
  if (!fieldName) return undefined;

  const lowerFieldName = fieldName.toLowerCase();

  // Credit card number
  if (
    lowerFieldName.includes('card_number') ||
    lowerFieldName.includes('cardnumber') ||
    (lowerFieldName.includes('card') && lowerFieldName.includes('number'))
  ) {
    return {
      mask: '0000 0000 0000 0000'
    };
  }

  // Expiration date (MM/YYYY)
  if (
    lowerFieldName.includes('exp') ||
    lowerFieldName.includes('expir') ||
    lowerFieldName.includes('expiry')
  ) {
    return {
      mask: '00/0000',
      type: 'text'
    };
  }

  // CVC/CVV - security code
  if (
    lowerFieldName.includes('cvc') ||
    lowerFieldName.includes('cvv') ||
    lowerFieldName.includes('security') ||
    lowerFieldName.includes('card_code') ||
    lowerFieldName.includes('verification')
  ) {
    return {
      mask: '0000',
      hiddenInput: true,
      type: 'password'
    };
  }

  // ZIP/Postal code
  if (lowerFieldName.includes('zip') || lowerFieldName.includes('postal')) {
    return {
      mask: '00000'
    };
  }

  // Phone number
  if (lowerFieldName.includes('phone') || lowerFieldName.includes('tel')) {
    return {
      mask: '(000) 000-0000'
    };
  }

  return undefined;
}

/**
 * Get autocomplete value for payment fields
 * @param fieldName - The name of the field
 * @returns Autocomplete attribute value
 */
export function getPaymentFieldAutocomplete(fieldName: string): string {
  if (!fieldName) return 'off';

  const lowerFieldName = fieldName.toLowerCase();

  // Credit card number
  if (
    lowerFieldName.includes('card_number') ||
    lowerFieldName.includes('cardnumber') ||
    (lowerFieldName.includes('card') && lowerFieldName.includes('number'))
  ) {
    return 'cc-number';
  }

  // Expiration date (MM/YYYY)
  if (
    lowerFieldName.includes('exp') ||
    lowerFieldName.includes('expir') ||
    lowerFieldName.includes('expiry')
  ) {
    return 'cc-exp';
  }

  // CVC/CVV - security code
  if (
    lowerFieldName.includes('cvc') ||
    lowerFieldName.includes('cvv') ||
    lowerFieldName.includes('csc') ||
    lowerFieldName.includes('security') ||
    lowerFieldName.includes('card_code') ||
    lowerFieldName.includes('verification')
  ) {
    return 'cc-csc';
  }

  // ZIP/Postal code
  if (lowerFieldName.includes('zip') || lowerFieldName.includes('postal')) {
    return 'postal-code';
  }

  return 'on';
}

/**
 * Get a custom mask configuration
 * @param mask - The mask pattern (e.g., "(000) 000-0000")
 * @param showMaskTyped - Whether to show the mask format while typing
 * @param hiddenInput - Whether to hide the input (like password)
 * @returns Mask configuration object
 */
export function getCustomMask(
  mask: string,
  showMaskTyped: boolean = false,
  hiddenInput: boolean = false
): FieldMaskConfig {
  return {
    mask,
    showMaskTyped,
    hiddenInput,
    type: hiddenInput ? 'password' : 'text'
  };
}
